import { useEffect, useState } from "react";

import { testSshConnection } from "../../api/sshConnection";

export type SshConnectionSettings = {
    sshHost: string;
    sshPort: string;
    sshUsername: string;
    sshAuthType: "PASSWORD" | "PRIVATE_KEY";
    sshPrivateKeyPath: string;
    strictHostKeyChecking: boolean;
};

type Props = {
    settings: SshConnectionSettings;
    siteId?: number;
    profileId?: number;
    credentialValue: string;
    onCredentialValueChange: (value: string) => void;
    savePassword: boolean;
    onSavePasswordChange: (value: boolean) => void;
    hasStoredPassword?: boolean;
    disabled?: boolean;
};

type TestResult = { type: "success" | "error"; message: string };

const getErrorMessage = (error: unknown) => {
    const data = (error as { response?: { data?: { message?: string; detail?: string } } }).response?.data;
    return data?.detail || data?.message || "SSH 연결에 실패했습니다.";
};

export default function SshConnectionTest({
    settings,
    siteId,
    profileId,
    credentialValue,
    onCredentialValueChange,
    savePassword,
    onSavePasswordChange,
    hasStoredPassword = false,
    disabled = false,
}: Props) {
    const [isTesting, setIsTesting] = useState(false);
    const [result, setResult] = useState<TestResult | null>(null);

    useEffect(() => setResult(null), [
        settings.sshAuthType,
        settings.sshHost,
        settings.sshPort,
        settings.sshUsername,
        settings.sshPrivateKeyPath,
        settings.strictHostKeyChecking,
    ]);

    const runTest = async () => {
        if (isTesting || disabled) return;
        const port = Number(settings.sshPort);
        if (!settings.sshHost.trim()) {
            setResult({ type: "error", message: "SSH 호스트를 입력해 주세요." });
            return;
        }
        if (!Number.isInteger(port) || port < 1 || port > 65535) {
            setResult({ type: "error", message: "SSH 포트는 1~65535 범위로 입력해 주세요." });
            return;
        }
        if (!settings.sshUsername.trim()) {
            setResult({ type: "error", message: "SSH 계정을 입력해 주세요." });
            return;
        }
        if (settings.sshAuthType === "PRIVATE_KEY" && !settings.sshPrivateKeyPath.trim()) {
            setResult({ type: "error", message: "개인키 경로를 입력해 주세요." });
            return;
        }
        if (settings.sshAuthType === "PASSWORD" && !credentialValue && !hasStoredPassword) {
            setResult({ type: "error", message: "테스트 비밀번호를 입력해 주세요." });
            return;
        }

        setIsTesting(true);
        setResult(null);
        try {
            const response = await testSshConnection({
                siteId: hasStoredPassword ? siteId : undefined,
                profileId: hasStoredPassword ? profileId : undefined,
                sshHost: settings.sshHost.trim(),
                sshPort: port,
                sshUsername: settings.sshUsername.trim(),
                sshAuthType: settings.sshAuthType,
                sshPrivateKeyPath: settings.sshAuthType === "PRIVATE_KEY" ? settings.sshPrivateKeyPath.trim() : undefined,
                strictHostKeyChecking: settings.strictHostKeyChecking,
                password: settings.sshAuthType === "PASSWORD" && credentialValue ? credentialValue : undefined,
                privateKeyPassphrase: settings.sshAuthType === "PRIVATE_KEY" && credentialValue ? credentialValue : undefined,
            });
            setResult({ type: "success", message: `연결 성공 · ${response.elapsedMs}ms` });
        } catch (error) {
            setResult({ type: "error", message: getErrorMessage(error) });
        } finally {
            setIsTesting(false);
        }
    };

    const isPassword = settings.sshAuthType === "PASSWORD";
    return (
        <div className="dp-ssh-test">
            <div className="dp-ssh-test-heading"><strong>연결 테스트</strong><span>저장 전에 SSH 인증 여부를 확인합니다.</span></div>
            <div className="dp-ssh-test-controls">
                <label className="dp-ssh-field">
                    <span>{isPassword ? "테스트 비밀번호" : "개인키 암호 (선택)"}</span>
                    <input
                        className="form-control"
                        type="password"
                        autoComplete="new-password"
                        value={credentialValue}
                        placeholder={isPassword && hasStoredPassword ? "저장된 비밀번호 사용 · 변경 시에만 입력" : isPassword ? "비밀번호 입력" : "암호가 있는 경우 입력"}
                        onChange={(event) => { onCredentialValueChange(event.currentTarget.value); setResult(null); }}
                        onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void runTest(); } }}
                        disabled={disabled || isTesting}
                    />
                </label>
                <button type="button" className="dp-ssh-test-button" onClick={() => void runTest()} disabled={disabled || isTesting}>
                    {isTesting ? "테스트 중" : "연결 테스트"}
                </button>
            </div>
            {isPassword && (
                <label className="dp-ssh-remember">
                    <input type="checkbox" aria-label="SSH 비밀번호 저장" checked={savePassword} onChange={(event) => onSavePasswordChange(event.currentTarget.checked)} disabled={disabled || isTesting} />
                    <span><strong>SSH 비밀번호 저장</strong><small>현재 Windows 사용자 계정으로 암호화해 저장합니다.</small></span>
                </label>
            )}
            {result && <div className={`dp-ssh-test-result is-${result.type}`} role={result.type === "error" ? "alert" : "status"}>{result.message}</div>}
        </div>
    );
}
